import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _objectWithoutProperties from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
var _excluded = ["type", "config", "className", "style", "inline", "ref"],
  _excluded2 = ["type", "config", "showLoader", "loaderComponent", "errorComponent", "className", "style", "inline", "customStyles", "amount", "ref", "currency"];
var __jsx = React.createElement;
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { checkFeatureFlag, checkTestFlag } from "@/common/config/websiteConfig";
import { TAMRA_WIDGET_URL } from "@globalConfig";
import { memo } from 'react';
// Create Tamara Context
var TamaraContext = /*#__PURE__*/createContext(null);

/**
 * Tamara Widget Provider - Global provider for Tamara widget functionality
 * Wrap your app with this provider at the root level
 */
export function TamaraProvider(_ref) {
  var children = _ref.children,
    publicKey = _ref.publicKey,
    _ref$lang = _ref.lang,
    lang = _ref$lang === void 0 ? "ar" : _ref$lang,
    _ref$country = _ref.country,
    country = _ref$country === void 0 ? "SA" : _ref$country,
    _ref$environment = _ref.environment,
    environment = _ref$environment === void 0 ? "sandbox" : _ref$environment,
    _ref$defaultWidgetCon = _ref.defaultWidgetConfig,
    defaultWidgetConfig = _ref$defaultWidgetCon === void 0 ? {
      theme: "light",
      badgePosition: "",
      showExtraContent: "",
      hidePayInX: false
    } : _ref$defaultWidgetCon;
  var _useState = useState(true),
    loading = _useState[0],
    setLoading = _useState[1];
  var _useState2 = useState(null),
    error = _useState2[0],
    setError = _useState2[1];
  var _useState3 = useState(false),
    ready = _useState3[0],
    setReady = _useState3[1];
  var configScriptRef = useRef(null);
  var widgetScriptRef = useRef(null);
  // Determine script URL based on environment
  var scriptUrl = TAMRA_WIDGET_URL;
  useEffect(function () {
    var mounted = true;
    if (checkTestFlag("TEST_ENABLE_TAMRA_WIDGET") !== undefined && !checkTestFlag("TEST_ENABLE_TAMRA_WIDGET")) {
      return;
    }
    if (!checkFeatureFlag("FF_ENABLE_TAMRA_WIDGET")) {
      return;
    }
    var loadTamaraWidget = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee() {
        var existingConfigScript, existingWidgetScript, configScript, widgetScript;
        return _regenerator().w(function (_context) {
          while (1) switch (_context.n) {
            case 0:
              try {
                setLoading(true);
                setError(null);

                // Check if scripts already exist
                existingConfigScript = document.getElementById('tamara-config');
                existingWidgetScript = document.querySelector("script[src=\"".concat(scriptUrl, "\"]")); // Remove existing scripts if they exist
                if (existingConfigScript) {
                  existingConfigScript.remove();
                }
                if (existingWidgetScript) {
                  existingWidgetScript.remove();
                }

                // Create and add configuration script
                configScript = document.createElement('script');
                configScript.id = 'tamara-config';
                configScript.innerHTML = "\n                  window.tamaraWidgetConfig = {\n                    lang: \"".concat(lang, "\",\n                    country: \"").concat(country, "\",\n                    publicKey: \"").concat(publicKey, "\"\n                  };\n                ");
                document.head.appendChild(configScript);
                configScriptRef.current = configScript;

                // Create and add widget script
                widgetScript = document.createElement('script');
                widgetScript.type = 'text/javascript';
                widgetScript.src = scriptUrl;
                document.head.appendChild(widgetScript);
                widgetScript.onload = function () {
                  if (mounted) {
                    setLoading(false);
                    setReady(true);
                  }
                };
                widgetScript.onerror = function () {
                  if (mounted) {
                    var err = new Error("Failed to load Tamara widget script from ".concat(scriptUrl));
                    setError(err);
                    setLoading(false);
                  }
                };
                widgetScriptRef.current = widgetScript;
              } catch (err) {
                if (mounted) {
                  setError(err);
                  setLoading(false);
                }
              }
            case 1:
              return _context.a(2);
          }
        }, _callee);
      }));
      return function loadTamaraWidget() {
        return _ref2.apply(this, arguments);
      };
    }();
    loadTamaraWidget();

    // Cleanup function
    return function () {
      mounted = false;

      // Remove scripts on unmount
      if (configScriptRef.current && configScriptRef.current.parentNode) {
        configScriptRef.current.parentNode.removeChild(configScriptRef.current);
      }
      if (widgetScriptRef.current && widgetScriptRef.current.parentNode) {
        widgetScriptRef.current.parentNode.removeChild(widgetScriptRef.current);
      }

      // Clean up global config
      if (window.tamaraWidgetConfig) {
        delete window.tamaraWidgetConfig;
      }
    };
  }, []);

  // Function to manually reload the widget
  var reloadWidget = function reloadWidget() {
    setReady(false);
    setLoading(true);
    setError(null);

    // Force re-initialization
    var event = new CustomEvent('tamara-reload');
    window.dispatchEvent(event);
  };

  // Function to update widget configuration
  var updateConfig = function updateConfig(newConfig) {
    if (window.tamaraWidgetConfig) {
      Object.assign(window.tamaraWidgetConfig, newConfig);
    }
  };

  // Component factory for the Tamara widget
  var createTamaraWidget = function createTamaraWidget() {
    var widgetProps = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var _widgetProps$type = widgetProps.type,
      type = _widgetProps$type === void 0 ? "tamara-summary" : _widgetProps$type,
      _widgetProps$config = widgetProps.config,
      config = _widgetProps$config === void 0 ? defaultWidgetConfig : _widgetProps$config,
      _widgetProps$classNam = widgetProps.className,
      className = _widgetProps$classNam === void 0 ? "" : _widgetProps$classNam,
      _widgetProps$style = widgetProps.style,
      style = _widgetProps$style === void 0 ? {} : _widgetProps$style,
      _widgetProps$inline = widgetProps.inline,
      inline = _widgetProps$inline === void 0 ? false : _widgetProps$inline,
      ref = widgetProps.ref,
      props = _objectWithoutProperties(widgetProps, _excluded);
    var configString = typeof config === 'object' ? JSON.stringify(config) : config;

    // Function to inject Cairo font styles into shadow root
    var injectCairoFont = function injectCairoFont(element) {
      if (!element) return;
      var _attemptStyleInjection = function attemptStyleInjection() {
        if (element.shadowRoot) {
          var styleElement = document.createElement('style');
          styleElement.textContent = tamaraCustomStyles;
          element.shadowRoot.prepend(styleElement);
        } else {
          // Retry if shadow root not ready yet
          setTimeout(_attemptStyleInjection, 50);
        }
      };

      // Wait for next tick to ensure element is mounted
      setTimeout(_attemptStyleInjection, 10);
    };

    // Create ref callback that handles both custom ref and style injection
    var handleRef = function handleRef(element) {
      if (element) {
        injectCairoFont(element);
      }

      // Handle user's ref if provided
      if (ref) {
        if (typeof ref === 'function') {
          ref(element);
        } else if (ref.current !== undefined) {
          ref.current = element;
        }
      }
    };
    return /*#__PURE__*/React.createElement('tamara-widget', _objectSpread({
      type: type,
      config: configString,
      className: "".concat(className, " ").concat(inline ? 'tamara-widget-inline' : '').trim(),
      style: _objectSpread({}, style),
      'data-inline': inline ? 'true' : 'false'
    }, props));
  };
  var contextValue = {
    loading: loading,
    error: error,
    ready: ready,
    createTamaraWidget: createTamaraWidget,
    reloadWidget: reloadWidget,
    updateConfig: updateConfig,
    config: {
      lang: lang,
      country: country,
      publicKey: publicKey,
      environment: environment
    }
  };
  return /*#__PURE__*/React.createElement(TamaraContext.Provider, {
    value: contextValue
  }, children);
}

/**
 * Hook to access Tamara widget functionality from anywhere in the app
 * Must be used within TamaraProvider
 */
export function useTamara() {
  return useContext(TamaraContext);
}

/**
 * Hook that returns a TamaraWidget component ready to render
 * @param {object} widgetConfig - Configuration for the widget
 * @returns {object} - Widget component, loading state, error state, and ready state
 */
export function useTamaraWidget() {
  var widgetConfig = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var _useTamara = useTamara(),
    loading = _useTamara.loading,
    error = _useTamara.error,
    ready = _useTamara.ready,
    createTamaraWidget = _useTamara.createTamaraWidget;
  var TamaraWidget = function TamaraWidget(props) {
    if (!ready) {
      return null; // or a loading placeholder
    }
    return createTamaraWidget(_objectSpread(_objectSpread({}, widgetConfig), props));
  };
  return {
    TamaraWidget: TamaraWidget,
    loading: loading,
    error: error,
    ready: ready
  };
}

/**
 * Ready-to-use Tamara Widget component
 * Can be used directly without hooks
 */

export var TamaraWidget = /*#__PURE__*/memo(function TamaraWidget(_ref3) {
  var _ref3$type = _ref3.type,
    type = _ref3$type === void 0 ? "tamara-summary" : _ref3$type,
    config = _ref3.config,
    _ref3$showLoader = _ref3.showLoader,
    showLoader = _ref3$showLoader === void 0 ? true : _ref3$showLoader,
    loaderComponent = _ref3.loaderComponent,
    errorComponent = _ref3.errorComponent,
    _ref3$className = _ref3.className,
    className = _ref3$className === void 0 ? "" : _ref3$className,
    _ref3$style = _ref3.style,
    style = _ref3$style === void 0 ? {} : _ref3$style,
    _ref3$inline = _ref3.inline,
    inline = _ref3$inline === void 0 ? false : _ref3$inline,
    _ref3$customStyles = _ref3.customStyles,
    customStyles = _ref3$customStyles === void 0 ? {} : _ref3$customStyles,
    amount = _ref3.amount,
    ref = _ref3.ref,
    currency = _ref3.currency,
    props = _objectWithoutProperties(_ref3, _excluded2);
  var _useTamara2 = useTamara(),
    loading = _useTamara2.loading,
    error = _useTamara2.error,
    ready = _useTamara2.ready,
    createTamaraWidget = _useTamara2.createTamaraWidget;
  // Apply inline styles if inline prop is true
  var containerStyle = _objectSpread(_objectSpread(_objectSpread({}, style), inline && {
    display: 'inline-block',
    width: 'auto',
    maxWidth: 'none'
  }), customStyles);
  var containerClassName = "".concat(className, " ").concat(inline ? 'tamara-widget-inline !bg-red' : '').trim();
  if (loading && showLoader) {
    return loaderComponent || /*#__PURE__*/React.createElement("div", {
      className: "tamara-loading"
    }, "Loading Tamara widget...");
  }
  if (error) {
    return errorComponent || /*#__PURE__*/React.createElement("div", {
      className: "tamara-error"
    }, "Error loading Tamara widget: ", error.message);
  }
  if (!ready) {
    return null;
  }
  return createTamaraWidget(_objectSpread({
    type: type,
    config: config,
    className: containerClassName,
    style: containerStyle,
    amount: amount,
    ref: ref
  }, props));
});
export var tamaraCustomStyles = "\n.tamara-summary-widget__container {\n    font-family: 'Cairo', sans-serif !important;\n    font-weight: 700 !important;\n}\n\n.tamara-summary-widget__inline__text {\n    display: none !important;\n}\n.tamara-widget__number-of-installments-node {\n    font-size: 14px !important;\n    font-weight: 500 !important;\n    font-family: 'Cairo', sans-serif !important;\n}\n\n";